/**
 * Client-side experiment ID for A/B tests (e.g. checkout_experiment).
 * Reads from URL param first, then from cookie (names from config/env) so returning users
 * (e.g. abandoned flow) still have the correct experiment_id.
 */

import { config } from '../config/env'
import { unmaskVariantId } from '../../../functions/ab-testing/config'

/**
 * Parse document.cookie into key-value object
 * @returns {Object<string, string>}
 */
function parseCookies() {
  const cookies = {}
  if (typeof document === 'undefined' || !document.cookie) return cookies
  document.cookie.split(';').forEach((cookie) => {
    const [name, ...rest] = cookie.trim().split('=')
    const value = rest.join('=').trim()
    if (name && value) {
      cookies[name] = decodeURIComponent(value)
    }
  })
  return cookies
}

/**
 * Get current experiment ID (variant name) for the user.
 * Uses URL param first, then cookie so it works on first load and on return (e.g. abandoned flow).
 * Param and cookie names come from env: VITE_AB_EXPERIMENT_URL_PARAM, VITE_AB_EXPERIMENT_COOKIE_NAME.
 * @returns {string|null} Experiment variant id (e.g. 'control', 'checkout_alt') or null
 */
export function getExperimentId() {
  if (typeof window === 'undefined') return null

  const urlParam = config.abExperimentUrlParam || '_vt'
  const cookieName = config.abExperimentCookieName || 'ab_checkout_experiment_v4'

  const fromUrl = new URLSearchParams(window.location.search).get(urlParam)
  if (fromUrl) {
    // Unmask the ID from URL (e.g., 'h2q9' -> 'checkout_v2')
    return unmaskVariantId(fromUrl)
  }

  const cookies = parseCookies()
  // Cookie already contains unmasked variant name
  return cookies[cookieName] || null
}
